import Icon from "@/components/Icon/Icon";
import { Text } from "@/components/Typography/Text";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { cn } from "@/lib/cn";
import { useCallback, useEffect, useState } from "react";
import { FilePreviewContent } from "./components/FilePreviewContent";

export interface FilePreviewModalProps {
  isOpen: boolean;
  onClose: () => void;
  filename: string;
  mimeType?: string;
  /**
   * ファイル内容を取得するためのコールバック関数
   * 戻り値はプレビュー用のURL（blob URLやAPI URL）
   */
  onGetFileUrl: () => Promise<string> | string;
  isLoading?: boolean;
  error?: string | null;
}

/**
 * ファイルプレビューモーダル
 */
export function FilePreviewModal({
  isOpen,
  onClose,
  filename,
  onGetFileUrl,
  isLoading = false,
  error = null,
}: FilePreviewModalProps) {
  const [localError, setLocalError] = useState<string | null>(null);
  const [imageUrl, setImageUrl] = useState<string | null>(null);

  // 統合されたエラー状態
  const displayError = error || localError;

  // 画像エラーハンドラー
  const handleImageError = useCallback(() => {
    setLocalError("画像の表示に失敗しました");
  }, []);

  useEffect(() => {
    if (!isOpen) {
      setImageUrl(null);
      setLocalError(null);
      return;
    }

    if (!isLoading && !displayError) {
      let isMounted = true;
      Promise.resolve(onGetFileUrl())
        .then((url) => {
          if (isMounted) setImageUrl(url);
        })
        .catch(() => {
          if (isMounted) setLocalError("ファイルのURL取得に失敗しました。");
        });
      return () => {
        isMounted = false;
      };
    }
  }, [isOpen, isLoading, displayError, onGetFileUrl]);

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent
        className={cn(
          "max-w-4xl max-h-[90vh] p-0 overflow-hidden",
          "data-[state=open]:animate-in data-[state=closed]:animate-out",
          "data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0",
          "data-[state=closed]:zoom-out-95 data-[state=open]:zoom-in-95",
        )}
        hideCloseButton
      >
        {/* ヘッダー */}
        <DialogHeader className="flex flex-row items-center justify-between p-4 border-b space-y-0">
          <DialogTitle className="truncate flex-1 mr-4" title={filename}>
            <Text variant="labelSemiLarge">{filename}</Text>
          </DialogTitle>
          <Button
            variant="ghost"
            size="icon"
            onClick={onClose}
            className="h-8 w-8 rounded-sm opacity-70 ring-offset-background transition-opacity hover:opacity-100 focus:outline-none focus:ring-2 focus:ring-ring focus:ring-offset-2"
            aria-label="プレビューを閉じる"
          >
            <Icon iconName="XIcon" className="w-4 h-4" />
            <span className="sr-only">Close</span>
          </Button>
        </DialogHeader>

        {/* コンテンツエリア */}
        <div className="p-4">
          <FilePreviewContent
            imageUrl={imageUrl}
            filename={filename}
            isLoading={isLoading}
            error={displayError}
            onImageError={handleImageError}
          />
        </div>
      </DialogContent>
    </Dialog>
  );
}
